/**
 * AiImageGenerator Component
 *
 * Dialog for generating AI images using Google Gemini.
 * Based on Figma design: https://www.figma.com/design/UlCtzXKPnAAma9QrgCWCge/Rituality---Web---Mob--Internal-?node-id=7882-44762
 */

import { useQuery } from "@tanstack/react-query"
import { Loader2 } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { Carousel } from "@/components/carousel/Carousel"
import { AIGeneratedIcon } from "@/components/icons"
import { RitualityIcon } from "@/components/shared/RitualityIcon"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useGenerateImage } from "@/features/media/hooks/useGenerateImage"
import type { GeneratedImage } from "@/features/media/services/ai-image.service"
import { getRecentImages, markImageUsed } from "@/features/media/services/ai-image.service"

interface EntityContext {
  name?: string
  description?: string
  type?: string
  tags?: string[]
}

interface AiImageGeneratorProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  entityType: "ritual" | "community" | "coaching"
  entityContext?: EntityContext
  onImageSelected: (file: File, generationId: string, imageIndex: number) => void
  maxFiles?: number
}

function buildAutoPrompt(
  entityType: "ritual" | "community" | "coaching",
  context?: EntityContext,
): string {
  if (!context) return `A beautiful ${entityType} cover image`

  const parts: string[] = []
  if (context.name) parts.push(context.name)
  if (context.description) {
    parts.push(context.description.slice(0, 100))
  }
  if (context.type) parts.push(`${context.type} ${entityType}`)
  if (context.tags?.length) parts.push(context.tags.slice(0, 3).join(", "))

  return parts.join(" - ") || `A beautiful ${entityType} cover image`
}

const MIME_TO_EXT: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
}

async function base64ToFile(base64: string, mimeType: string, index: number): Promise<File> {
  const res = await fetch(`data:${mimeType};base64,${base64}`)
  const blob = await res.blob()
  const extension = MIME_TO_EXT[mimeType] || "png"
  return new File([blob], `ai-generated-${Date.now()}-${index}.${extension}`, { type: mimeType })
}

export function AiImageGenerator({
  open,
  onOpenChange,
  entityType,
  entityContext,
  onImageSelected,
  maxFiles,
}: AiImageGeneratorProps) {
  const autoPrompt = useMemo(
    () => buildAutoPrompt(entityType, entityContext),
    [entityType, entityContext],
  )
  const [prompt, setPrompt] = useState(autoPrompt)
  const [isLoading, setIsLoading] = useState(false)
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([])
  const [generationId, setGenerationId] = useState<string | null>(null)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [selectedRecentId, setSelectedRecentId] = useState<string | null>(null)
  const [hasGenerated, setHasGenerated] = useState(false)

  const { mutateAsync, error } = useGenerateImage()

  const { data: recentImages = [] } = useQuery({
    queryKey: ["ai-generated-images", "recent"],
    queryFn: () => getRecentImages(5),
    enabled: open,
    staleTime: 0,
  })

  useEffect(() => {
    if (open) {
      setPrompt(autoPrompt)
      setIsLoading(false)
      setGeneratedImages([])
      setGenerationId(null)
      setSelectedIndex(null)
      setSelectedRecentId(null)
      setHasGenerated(false)
    }
  }, [open, autoPrompt])

  const handleGenerate = async () => {
    if (!prompt.trim()) return

    setIsLoading(true)
    setSelectedIndex(null)
    setSelectedRecentId(null)

    try {
      const result = await mutateAsync({ entityType, prompt })
      setGeneratedImages(result.images)
      setGenerationId(result.generationId)
      setSelectedIndex(0)
      setHasGenerated(true)
    } catch {
      // Error handled by hook
    } finally {
      setIsLoading(false)
    }
  }

  const handleUseImage = async () => {
    if (selectedIndex === null || !generatedImages[selectedIndex] || !generationId) return
    if (selectedRecentId) return

    const img = generatedImages[selectedIndex]
    const file = await base64ToFile(img.base64, img.mimeType, selectedIndex)
    await markImageUsed(generationId, selectedIndex)
    onImageSelected(file, generationId, selectedIndex)
    onOpenChange(false)
  }

  const handleUseRecent = async () => {
    if (!selectedRecentId) return

    const recentImg = recentImages.find(img => img.id === selectedRecentId)
    if (!recentImg) return

    const file = await base64ToFile(recentImg.image, "image/png", recentImg.indexNumber)
    await markImageUsed(recentImg.generationId, recentImg.indexNumber)
    onImageSelected(file, recentImg.generationId, recentImg.indexNumber)
    onOpenChange(false)
  }

  const isUseDisabled = selectedIndex === null && selectedRecentId === null

  const errorMessage = error
    ? (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
      error.message
    : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg max-h-[90vh] overflow-y-auto p-4"
        onInteractOutside={e => e.preventDefault()}
      >
        <DialogHeader className="pt-5 px-3.5">
          <DialogTitle className="text-[24px] font-semibold pb-0">Generate Images</DialogTitle>
          <DialogDescription className="text-[14px] text-muted-foreground leading-[25px]">
            Generate a unique image for your {entityType}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Prompt Section */}
          <div className="space-y-2 px-3">
            <label className="text-[12px] font-medium uppercase leading-[12px]">
              Describe your image *
            </label>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder={`Describe your ${entityType} cover image...`}
              className="w-full min-h-[35px] rounded-none border-b border-border bg-transparent py-1 text-[12px] text-primary placeholder:text-muted-foreground focus:outline-none resize-none leading-[20px]"
              rows={2}
            />
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              {errorMessage}
            </div>
          )}

          {/* Image Grid */}
          {isLoading ? (
            <div className="px-2">
              <div className="h-[302.5px] bg-base rounded-tl-2xl rounded-br-2xl flex items-center justify-center">
                <div className="border border-white p-4 rounded-full">
                  <AIGeneratedIcon className="w-8 h-8 text-white animate-spin" />
                </div>
              </div>
            </div>
          ) : generatedImages.length > 0 ? (
            <div className="px-2 overflow-y-auto" style={{ maxHeight: "302.5px" }}>
              <div className="flex flex-col gap-3">
                {generatedImages.map((img, i) => (
                  <button
                    key={`gen-${i}`}
                    type="button"
                    onClick={() => {
                      setSelectedIndex(i)
                      setSelectedRecentId(null)
                    }}
                    className={`relative h-[302.5px] rounded-lg overflow-hidden border-2 transition-all cursor-pointer focus:outline-none w-full ${
                      selectedIndex === i
                        ? "border-primary"
                        : "border-transparent hover:border-border"
                    }`}
                  >
                    <img
                      src={`data:${img.mimeType};base64,${img.base64}`}
                      alt={`Generated option ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {selectedIndex === i && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-primary-foreground"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {/* Action Buttons */}
          <div className="flex gap-3 px-2">
            {!hasGenerated ? (
              <Button
                onClick={handleGenerate}
                disabled={isLoading || !prompt.trim() || (maxFiles !== undefined && maxFiles <= 0)}
                className="flex-1 uppercase"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  "Generate image"
                )}
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={handleGenerate}
                  disabled={isLoading}
                  className="flex-1 uppercase"
                >
                  <RitualityIcon size={20} className="mr-2 !w-5 !h-5" />
                  Regenerate
                </Button>
                <Button
                  onClick={selectedRecentId ? handleUseRecent : handleUseImage}
                  disabled={isUseDisabled || isLoading}
                  className="flex-1 uppercase"
                >
                  Use
                </Button>
              </>
            )}
          </div>

          {/* Recent Generations */}
          {recentImages.length > 0 && (
            <div className="space-y-2 px-2 pt-0">
              <p className="text-sm font-medium text-body">Recent generations</p>
              <Carousel
                data={recentImages}
                className="w-[300px] sm:w-full"
                renderItem={img => (
                  <button
                    type="button"
                    onClick={async () => {
                      setSelectedRecentId(img.id)
                      setSelectedIndex(null)
                      const file = await base64ToFile(img.image, "image/png", img.indexNumber)
                      await markImageUsed(img.generationId, img.indexNumber)
                      onImageSelected(file, img.generationId, img.indexNumber)
                      onOpenChange(false)
                    }}
                    className={`relative w-21.5 h-16 rounded-lg overflow-hidden border-2 transition-all cursor-pointer focus:outline-none ${
                      selectedRecentId === img.id
                        ? "border-primary"
                        : "border-transparent hover:border-border"
                    }`}
                  >
                    <img
                      src={`data:image/png;base64,${img.image}`}
                      alt="Recent generated"
                      className="w-full h-full object-cover"
                    />
                  </button>
                )}
                itemWidth="86px"
                contentClassName="gap-2 touch-pan-x"
                opts={{ dragFree: true, loop: false, containScroll: "trimSnaps" }}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export type { EntityContext }
